import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';
import { useSeo } from '@/lib/seo';

function Stat({ label, value, sub }) {
  return (
    <Card className="border-[hsl(var(--border))]">
      <CardContent className="p-5">
        <p className="kicker">{label}</p>
        <p className="font-serif text-3xl tracking-tight mt-1">{value}</p>
        {sub && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  useSeo({ title: 'Dashboard | Admin' });
  const [stats, setStats] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [alsoSubs, setAlsoSubs] = useState(false);
  const [alsoMsgs, setAlsoMsgs] = useState(false);
  const [alsoMedia, setAlsoMedia] = useState(false);

  const loadStats = () => adminApi.stats().then(setStats).catch(() => {});
  useEffect(() => {
    loadStats();
  }, []);

  const runReset = async () => {
    setResetting(true);
    try {
      const res = await adminApi.resetContent({
        include_subscribers: alsoSubs,
        include_messages: alsoMsgs,
        include_media: alsoMedia,
      });
      toast.success('Content reset. Placeholders are gone and locked out of future deploys.');
      // reload
      setResetOpen(false);
      setConfirmText('');
      setAlsoSubs(false);
      setAlsoMsgs(false);
      setAlsoMedia(false);
      loadStats();
      console.info('reset counts', res?.counts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const canConfirm = confirmText.trim().toUpperCase() === 'RESET';

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="kicker">Dashboard</p>
          <h1 className="font-serif text-3xl tracking-tight">The season, at a glance.</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/season/new">
            <Button>+ New Season Entry</Button>
          </Link>
          <Link to="/admin/field-notes/new">
            <Button variant="secondary">+ New Field Note</Button>
          </Link>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Subscribers" value={stats?.subscribers ?? '—'} sub="The Field Report list" />
        <Stat
          label="Unread messages"
          value={stats?.contact_messages?.unread ?? '—'}
          sub={`${stats?.contact_messages?.total ?? 0} total`}
        />
        <Stat
          label="Season entries"
          value={stats?.season_entries?.published ?? '—'}
          sub={`${stats?.season_entries?.drafts ?? 0} drafts`}
        />
        <Stat
          label="Field notes"
          value={stats?.field_notes?.published ?? '—'}
          sub={`${stats?.field_notes?.drafts ?? 0} drafts`}
        />
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6">
          <p className="kicker">Quick actions</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Link
              to="/admin/featured-video"
              className="rounded-lg border border-[hsl(var(--border))] p-3 hover:bg-[hsl(var(--accent)/0.2)]"
            >
              <p className="font-medium">Update featured video</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Swap the video shown on the home page.
              </p>
            </Link>
            <Link
              to="/admin/site-config"
              className="rounded-lg border border-[hsl(var(--border))] p-3 hover:bg-[hsl(var(--accent)/0.2)]"
            >
              <p className="font-medium">Site configuration</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Social links, contact email, sender email, season openers.
              </p>
            </Link>
            <Link
              to="/admin/subscribers"
              className="rounded-lg border border-[hsl(var(--border))] p-3 hover:bg-[hsl(var(--accent)/0.2)]"
            >
              <p className="font-medium">Subscribers</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Review or export The Field Report list.
              </p>
            </Link>
            <Link
              to="/admin/inbox"
              className="rounded-lg border border-[hsl(var(--border))] p-3 hover:bg-[hsl(var(--accent)/0.2)]"
            >
              <p className="font-medium">Inbox</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Read messages from the contact form.
              </p>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-2 border-destructive/40 bg-destructive/[0.03]">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={20}
              className="mt-1 shrink-0 text-destructive"
              aria-hidden="true"
            />
            <div className="flex-1">
              <p className="kicker text-destructive">Danger zone</p>
              <h2 className="font-serif text-xl tracking-tight mt-1">Reset placeholder content</h2>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                Deletes every Season Entry, Field Note, Recap, saved revision, and blanks the
                featured video. Also locks out the auto-seed so the placeholders never come back on
                future deploys. Your admin account, site config, and subscribers are kept unless
                you opt in below.
              </p>
              <div className="mt-4">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setResetOpen(true)}
                  data-testid="reset-content-open-button"
                >
                  <Trash2 size={14} className="mr-1.5" /> Reset content…
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all placeholder content?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every Season Entry, Field Note, Recap, and saved revision.
              Featured video will be blanked. This action can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2">
              <Checkbox
                id="reset-subs"
                checked={alsoSubs}
                onCheckedChange={(v) => setAlsoSubs(Boolean(v))}
                data-testid="reset-include-subscribers"
              />
              <Label htmlFor="reset-subs" className="cursor-pointer text-sm">
                Also delete <strong>subscribers</strong>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="reset-msgs"
                checked={alsoMsgs}
                onCheckedChange={(v) => setAlsoMsgs(Boolean(v))}
                data-testid="reset-include-messages"
              />
              <Label htmlFor="reset-msgs" className="cursor-pointer text-sm">
                Also delete <strong>contact messages</strong>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="reset-media"
                checked={alsoMedia}
                onCheckedChange={(v) => setAlsoMedia(Boolean(v))}
                data-testid="reset-include-media"
              />
              <Label htmlFor="reset-media" className="cursor-pointer text-sm">
                Also delete <strong>uploaded images</strong> (media files)
              </Label>
            </div>
            <div className="pt-2">
              <Label htmlFor="reset-confirm" className="text-sm">
                Type <code className="font-mono font-semibold">RESET</code> to confirm
              </Label>
              <input
                id="reset-confirm"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                data-testid="reset-confirm-input"
                autoComplete="off"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canConfirm || resetting}
              onClick={runReset}
              data-testid="reset-content-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 size={14} className="mr-1.5" />
              {resetting ? 'Resetting…' : 'Delete everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
